import {
  SapAccountAddress,
  SapNewOrder,
  SapNewOrderSchema,
  SapPurchasableItem,
} from '@allurion/domain';
import {
  Card,
  FormGroup,
  InputSelector,
  Table,
  TextArea,
  TextInput,
  FormError,
} from '@allurion/ui';
import { formatMoney } from '@allurion/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { TrackedPageHeader } from 'src/analytics/TrackedUI';
import { Loader } from 'src/components/ui/Loader';
import { toastError } from 'src/components/ui/toasts';
import { useLocale } from 'src/hooks/useLocale';
import { Logger } from 'src/services/Logger';

import messages from './clinic-orders.messages';

import styles from './OrderForm.module.scss';

const BALLOON_DEVICE_SKUS = ['FS010-26', 'FS010-47', 'FS010-50'] as const;
const BALLOON_FILLER_KIT_SKU = 'FD011-12';
const BALLOON_SCALE_SKU = 'FD015-01';
const BALLOON_DEVICE_SKUS_LABEL = BALLOON_DEVICE_SKUS.join(' / ');

function getSkuQuantity(items: SapNewOrder['items'], sku: string) {
  return items
    .filter((item) => item.productId === sku)
    .reduce((total, item) => total + item.quantity, 0);
}

type Props = {
  purchasableItems: SapPurchasableItem[];
  onSubmit: (data: SapNewOrder) => void;
  billingAddresses: SapAccountAddress[];
  shippingAddresses: SapAccountAddress[];
  accountName: string | undefined;
  goBack?: () => void;
};

export function OrderForm({
  purchasableItems,
  onSubmit,
  billingAddresses,
  shippingAddresses,
  accountName,
  goBack,
}: Props) {
  const intl = useIntl();
  const { locale } = useLocale();
  const [isSaving, setIsSaving] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { errors },
  } = useForm<SapNewOrder>({
    resolver: zodResolver(SapNewOrderSchema),
    defaultValues: {
      accountId: 'temp',
      items: purchasableItems.map(({ price, productId, currencyCode, description }) => ({
        productId: productId!,
        description: description!,
        price,
        currencyCode,
        quantity: 0,
      })),
    },
  });

  const items = watch('items');
  const deviceQuantity = BALLOON_DEVICE_SKUS.reduce((total, sku) => {
    return total + getSkuQuantity(items, sku);
  }, 0);
  const fillerKitQuantity = getSkuQuantity(items, BALLOON_FILLER_KIT_SKU);
  const scaleQuantity = getSkuQuantity(items, BALLOON_SCALE_SKU);
  const hasRatioSkus = deviceQuantity > 0 || fillerKitQuantity > 0 || scaleQuantity > 0;
  const showRatioWarning =
    hasRatioSkus && !(deviceQuantity === fillerKitQuantity && fillerKitQuantity === scaleQuantity);

  const totalValue = items.reduce((acc, item) => {
    return acc + item.quantity * item.price;
  }, 0);

  return (
    <form onSubmit={handleSubmit(submit)}>
      <Loader cover isLoading={isSaving} />
      <TrackedPageHeader
        title={intl.formatMessage(messages.newOrderTitle)}
        button={{
          label: intl.formatMessage(messages.saveBtnLabel),
          trackLabel: 'create-balloon-order-submit',
          type: 'submit',
          onClick: handleSubmit(submit),
        }}
        onNavButtonClick={goBack}
      />
      <div className={styles.container}>
        <Card title={intl.formatMessage(messages.itemsCardTitle)}>
          <Table
            columns={[
              {
                title: intl.formatMessage(messages.productDescriptionColumn),
                field: 'description',
                formatter: (name) =>
                  name ?? <span>{intl.formatMessage(messages.missingProductDescription)}</span>,
              },
              {
                title: intl.formatMessage(messages.productIdColumn),
                field: 'productId',
              },
              {
                title: intl.formatMessage(messages.priceColumn),
                field: 'price',
                align: 'right',
                formatter: (_, { price, currencyCode }) => formatMoney(price, currencyCode, locale),
              },
              {
                title: intl.formatMessage(messages.quantityColumn),
                field: 'quantity',
                align: 'right',
                formatter: (_, __, index) => (
                  <div style={{ display: 'flex', justifyContent: 'right' }}>
                    <TextInput
                      type="number"
                      {...register(`items.${index}.quantity`, {
                        valueAsNumber: true,
                      })}
                      style={{ width: '100px', maxWidth: '100px' }}
                    />
                  </div>
                ),
              },
              {
                title: intl.formatMessage(messages.totalColumn),
                field: 'quantity',
                align: 'right',
                formatter: (_, { price, currencyCode, quantity }) => {
                  const total = quantity * price;

                  return formatMoney(total, currencyCode, locale);
                },
              },
            ]}
            data={items}
            keyField="productId"
          />
          <FormError error={errors.items?.message} />
          {showRatioWarning && (
            <p className={styles.warning}>
              {intl.formatMessage(messages.balloonRatioWarning, {
                deviceSkus: BALLOON_DEVICE_SKUS_LABEL,
                fillerKitSku: BALLOON_FILLER_KIT_SKU,
                scaleSku: BALLOON_SCALE_SKU,
              })}
            </p>
          )}
          <p style={{ margin: '1rem', textAlign: 'right', fontSize: '1.5rem', color: '#00363f' }}>
            {intl.formatMessage(messages.subtotalLabel)}:{' '}
            {formatMoney(totalValue, purchasableItems[0].currencyCode, locale)}
          </p>
          <small>{intl.formatMessage(messages.subtotalDisclaimer)}</small>
        </Card>

        <div className={styles.row}>
          <Card title={intl.formatMessage(messages.shippingInformationCardTitle)}>
            <Controller
              name="shippingAddress"
              control={control}
              render={({ field: { onChange, value } }) => (
                <>
                  {shippingAddresses.map((address, index) => (
                    <div
                      key={index}
                      style={{
                        marginBottom: '0.2rem',
                        border: '1px solid #ccc',
                        padding: '1rem',
                        display: 'flex',
                        flexDirection: 'row',
                      }}
                    >
                      <InputSelector
                        type="radio"
                        label={
                          <div>
                            <strong>{intl.formatMessage(messages.addressLineLabel)}: </strong>
                            {address.line1}, {address.line2},{address.city}, {address.postalCode},{' '}
                            {address.country}
                            <br />
                            <strong>{intl.formatMessage(messages.phoneNumberLabel)}: </strong>{' '}
                            {address.phone}
                            <br />
                            <strong>{intl.formatMessage(messages.emailLabel)}: </strong>{' '}
                            {address.email}
                          </div>
                        }
                        value={JSON.stringify(address)}
                        checked={JSON.stringify(address) === JSON.stringify(value)}
                        onChange={(e) => {
                          const selectedAddress = JSON.parse(e.target.value);

                          onChange(selectedAddress);
                        }}
                      />
                    </div>
                  ))}
                  <FormError error={errors.shippingAddress?.message} />
                </>
              )}
            />

            <FormGroup
              label={intl.formatMessage(messages.shippingInstructionsLabel)}
              error={errors.shippingInstructions?.message}
            >
              <div className={styles.row}>
                <InputSelector
                  type="radio"
                  label={intl.formatMessage(messages.expressServiceLabel)}
                  value="express"
                  {...register('shippingInstructions')}
                />
                <InputSelector
                  type="radio"
                  label={intl.formatMessage(messages.economyServiceLabel)}
                  value="economy"
                  {...register('shippingInstructions')}
                />
              </div>
            </FormGroup>

            <FormGroup
              label={intl.formatMessage(messages.expectedDeliveryDateLabel)}
              error={errors.expectedDeliveryDate?.message}
            >
              <TextInput type="date" {...register('expectedDeliveryDate')} style={{}} />
            </FormGroup>
          </Card>

          <Card title={intl.formatMessage(messages.billingInformationCardTitle)}>
            <Controller
              name="billingAddress"
              control={control}
              render={({ field: { onChange, value } }) => (
                <>
                  {accountName && <strong>{accountName}</strong>}
                  {billingAddresses.map((address, index) => (
                    <div
                      key={index}
                      style={{
                        marginBottom: '0.5rem',
                        border: '1px solid #ccc',
                        padding: '1rem',
                        display: 'flex',
                        flexDirection: 'row',
                      }}
                    >
                      <InputSelector
                        type="radio"
                        label={
                          <div>
                            <strong>{intl.formatMessage(messages.addressLineLabel)}: </strong>
                            {address.line1}, {address.line2},{address.city}, {address.postalCode},{' '}
                            {address.country}
                            <br />
                            <strong>{intl.formatMessage(messages.phoneNumberLabel)}: </strong>{' '}
                            {address.phone}
                            <br />
                            <strong>{intl.formatMessage(messages.emailLabel)}: </strong>{' '}
                            {address.email}
                          </div>
                        }
                        value={JSON.stringify(address)}
                        checked={JSON.stringify(address) === JSON.stringify(value)}
                        onChange={(e) => {
                          const selectedAddress = JSON.parse(e.target.value);

                          onChange(selectedAddress);
                        }}
                      />
                    </div>
                  ))}
                  <FormError error={errors.billingAddress?.message} />
                </>
              )}
            />

            <FormGroup
              label={intl.formatMessage(messages.vatIdLabel)}
              help={intl.formatMessage(messages.vatIdHelp)}
              error={errors.vatId?.message}
            >
              <TextInput {...register('vatId')} />
            </FormGroup>

            <FormGroup
              label={intl.formatMessage(messages.overallDiscountLabel)}
              help={intl.formatMessage(messages.overallDiscountHelp)}
              error={errors.overallDiscount?.message}
            >
              <TextInput
                {...register('overallDiscount')}
                type="number"
                step="0.1"
                min="0"
                max="30"
              />
            </FormGroup>
          </Card>
        </div>

        <Card title={intl.formatMessage(messages.additionalInstructionsCardTitle)}>
          <FormGroup
            label={intl.formatMessage(messages.notesLabel)}
            help={intl.formatMessage(messages.notesHelp)}
            error={errors.notes?.message}
          >
            <TextArea
              placeholder={intl.formatMessage(messages.notesLabel)}
              {...register('notes')}
              style={{ width: '100%', height: '100px' }}
            />
          </FormGroup>

          <FormGroup
            label={intl.formatMessage(messages.externalReferenceLabel)}
            help={intl.formatMessage(messages.externalReferenceHelp)}
            error={errors.externalReference?.message}
          >
            <TextInput {...register('externalReference')} />
          </FormGroup>

          <p className={styles.terms}>
            {intl.formatMessage(messages.termsAndConditionsText)}:{' '}
            <a href="https://app.allurion.com/allurion-terms-conditions.html">
              https://app.allurion.com/allurion-terms-conditions.html
            </a>
          </p>
        </Card>
      </div>
    </form>
  );

  async function submit(data: SapNewOrder) {
    setIsSaving(true);
    try {
      data.items = data.items.filter((item) => item.quantity > 0);
      await onSubmit(data);
    } catch (error: any) {
      toastError(intl.formatMessage(messages.errorSubmittingOrder));
      Logger.captureException(error);
    }
    setIsSaving(false);
  }
}
